import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cx } from "shared/utils";
import { sanitizeEmailHtml } from "shared/utils/sanitize-email-html";

const DEFAULT_MIN_HEIGHT = 0;
const ROOT_ID = "email-root";
const MEASURE_WIDTHS = [2000, 1400];

// No `allow-scripts`, so nothing in the body runs. `allow-popups` is what makes
// links open at all — <base target="_blank"> alone is blocked in a sandbox.
const SANDBOX = "allow-same-origin allow-popups allow-popups-to-escape-sandbox";

const buildDocument = (bodyHtml: string, centered: boolean) => `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <base target="_blank" />
    <meta name="color-scheme" content="light" />
    <style>
      html, body { margin: 0; padding: 0; background: #ffffff; overflow: hidden; }
      html[data-fluid], html[data-fluid] body { background: transparent; }
      body {
        font-family: Helvetica, Arial, sans-serif;
        font-size: 14px;
        color: #0a0a0a;
        overflow-wrap: break-word;
      }
      a { color: #e6007e; text-decoration: underline; }
      /* display:table shrink-wraps, so this measures what the email wants. */
      #${ROOT_ID} {
        display: table;
        ${centered ? "margin: 0 auto;" : ""}
        transform-origin: top left;
      }
      /* Authors pin both width and height inline, so a capped image keeps its
         old height and distorts unless height is forced back to auto. */
      img { max-width: 100%; height: auto !important; }
    </style>
  </head>
  <body><div id="${ROOT_ID}">${bodyHtml}</div></body>
</html>`;

/**
 * Returns the width the email wants to lay out at, or 0 if it has none.
 *
 * Measured against deliberately over-wide roots: a canvas is usually declared
 * as `max-width:600px;width:100%`, which reports nothing but the width it was
 * already given. Only tables count — an image bigger than the canvas is meant
 * to be capped by it, not to widen it.
 *
 * Fluid content is anything whose width tracks the measuring width — a
 * width:100% table, or an image capped by max-width. Comparing against the
 * measuring width itself is not enough: inside a quoted reply (Gmail indents
 * the quote with a padded blockquote) fluid content lands just short of it
 * and would read as a near-2000px canvas, scaling the whole email down.
 */
const measureCanvas = (root: HTMLElement) => {
  const widthsAt = (selector: string, measureWidth: number) => {
    root.style.width = `${measureWidth}px`;
    return Array.from(root.querySelectorAll<HTMLElement>(selector)).map(
      (el) => el.offsetWidth
    );
  };

  const fixedWidest = (selector: string) => {
    const first = widthsAt(selector, MEASURE_WIDTHS[0]);
    const second = widthsAt(selector, MEASURE_WIDTHS[1]);
    let canvas = 0;
    first.forEach((width, i) => {
      if (width === second[i]) canvas = Math.max(canvas, width);
    });
    return canvas;
  };

  const canvas = fixedWidest("table") || fixedWidest("img");

  root.style.width = "";
  return canvas;
};

/**
 * Renders an email body as its sender laid it out. Emails are built on a fixed
 * canvas (usually 580px); where that doesn't fit, the whole thing is scaled
 * down rather than reflowed into a layout the recipient never saw.
 */
export const EmailFrame = ({
  html,
  title = "Email body",
  minHeight = DEFAULT_MIN_HEIGHT,
  centered = false,
  onCanvasWidth,
  transparentWhenFluid = false,
  className
}: {
  html: string;
  title?: string;
  minHeight?: number;
  /** Centres the email when the frame is wider than it — for a fixed-width preview pane. */
  centered?: boolean;
  /** Reports the canvas the email declares, or 0 if it declares none. */
  onCanvasWidth?: (width: number) => void;
  transparentWhenFluid?: boolean;
  className?: string;
}) => {
  const [height, setHeight] = useState(minHeight);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const docObserverRef = useRef<ResizeObserver>();
  const frameObserverRef = useRef<ResizeObserver>();
  const reportWidthRef = useRef(onCanvasWidth);
  reportWidthRef.current = onCanvasWidth;

  const srcDoc = useMemo(
    () => buildDocument(sanitizeEmailHtml(html), centered),
    [html, centered]
  );

  const fit = useCallback(() => {
    const frame = frameRef.current;
    const root = frame?.contentDocument?.getElementById(ROOT_ID);
    if (!frame || !root) return;

    // Measure unscaled, otherwise each pass would compound the previous scale.
    root.style.transform = "none";
    const canvas = measureCanvas(root);
    const naturalHeight = root.scrollHeight;

    const available = frame.clientWidth;
    // No canvas of its own (plain text, or a table that just fills what it is
    // given) means the email takes whatever width it has.
    const naturalWidth = canvas || available;
    const scale =
      naturalWidth > available && available > 0 ? available / naturalWidth : 1;

    root.style.marginLeft = canvas ? "" : "0";
    root.style.transform = scale === 1 ? "none" : `scale(${scale})`;
    root.ownerDocument.documentElement.toggleAttribute(
      "data-fluid",
      transparentWhenFluid && !canvas
    );
    setHeight(Math.max(Math.ceil(naturalHeight * scale), minHeight));
    reportWidthRef.current?.(canvas);
  }, [minHeight, transparentWhenFluid]);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;

    // fit() is idempotent, so re-running it settles rather than loops.
    frameObserverRef.current = new ResizeObserver(fit);
    frameObserverRef.current.observe(frame);

    return () => frameObserverRef.current?.disconnect();
  }, [fit]);

  useEffect(() => () => docObserverRef.current?.disconnect(), []);

  // Images settle after load and change the layout, so measuring once lands short.
  const handleLoad = useCallback(() => {
    const doc = frameRef.current?.contentDocument;
    const root = doc?.getElementById(ROOT_ID);
    if (!doc || !root) return;

    fit();
    docObserverRef.current?.disconnect();
    docObserverRef.current = new ResizeObserver(fit);
    docObserverRef.current.observe(root);
  }, [fit]);

  return (
    <iframe
      ref={frameRef}
      srcDoc={srcDoc}
      title={title}
      sandbox={SANDBOX}
      onLoad={handleLoad}
      className={cx("w-full border-0", className)}
      // content-box so a padding class on the frame adds around the measured
      // content height instead of eating into it (Tailwind's global border-box
      // would otherwise collapse the document viewport under the padding).
      style={{ height: `${height}px`, boxSizing: "content-box" }}
    />
  );
};
